import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  Inject,
  Injector,
  input,
  Optional,
  output,
  runInInjectionContext,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { connectState } from '@examdojo/angular/util';
import { IconComponent } from '@examdojo/core/icon';
import {
  isCollabInitialContentSeeded,
  markCollabInitialContentSeeded,
  PreventBlockMergeExtension,
  TiptapEditorOutputFormat,
  TiptapTableOfContentsComponent,
} from '@examdojo/core/tiptap';
import {
  type CollabConfig,
  observeThreads,
  TiptapEditorWrapperComponent,
  TiptapThreadsPanelComponent,
} from '@examdojo/core/tiptap-react-ui-components';
import type { UploadFunction } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/image-upload-node/image-upload-node-extension';
import { ImageUploadNode } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/image-upload-node/image-upload-node-extension';
import type { TiptapSmartLinkConfig } from '@examdojo/core/tiptap-react-ui-components/contexts/smart-link-context';
import { MAX_FILE_SIZE } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { provideFaIcons } from '@examdojo/icons';
import { distinctUntilDeepChanged } from '@examdojo/rxjs';
import { SMART_LINK_CONFIG, SmartLinkConfig, toReactSmartLinkConfig } from '@examdojo/smart-link';
import { EditorWithPopoverService, PopoverExtension, PopoverService } from '@examdojo/tiptap-popover';
import { isNotNullish } from '@examdojo/util/nullish';
import { faEye, faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { JSONContent } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import {
  combineLatest,
  concat,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { contentBlocksToTiptap, tiptapToContentBlocks } from '../converters';
import { ContentBlock } from '../model';
import { ContentBlocksRendererComponent } from '../renderer';
import { ContentBlockTocComponent } from '../renderer/table-of-contents';
import { AccordionExtension, AccordionPanelExtension } from './extensions/accordion';
import { ColumnExtension, ColumnsExtension } from './extensions/columns';
import { CustomComponentExtension } from './extensions/custom-component';
import { CustomIconExtension } from './extensions/custom-icon';
import { DataDisplayExtension } from './extensions/data-display';
import { EchartsExtension } from './extensions/echarts';
import { FormulaBookletUrlService, FormulaCalloutExtension } from './extensions/formula-callout';
import { GojsDiagramExtension } from './extensions/gojs-diagram';
import { KeyPointCalloutExtension } from './extensions/key-point-callout';
import { SimpleCalloutExtension } from './extensions/simple-callout';
import { SimpleSolverExtension } from './extensions/simple-solver';
import { SolverExtension } from './extensions/solver';
import { SuperLatexExtension } from './extensions/super-latex';
import { WidgetExtension } from './extensions/widget';
import { STUDY_NOTES_SLASH_MENU_ITEMS } from './slash-menu-items';

@Component({
  selector: 'dojo-study-notes-editor',
  templateUrl: './study-notes-editor.component.html',
  styleUrls: ['./study-notes-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full' },
  imports: [
    TiptapEditorWrapperComponent,
    TiptapThreadsPanelComponent,
    TiptapTableOfContentsComponent,
    ReactiveFormsModule,
    ContentBlocksRendererComponent,
    ContentBlockTocComponent,
    MatButtonToggleModule,
    IconComponent,
    TranslocoPipe,
  ],
  providers: [PopoverService, FormulaBookletUrlService],
})
export class StudyNotesEditorComponent {
  constructor(
    private readonly injector: Injector,
    private readonly popoverService: PopoverService,
    private readonly formulaBookletUrlService: FormulaBookletUrlService,
    private readonly errorHandlerService: ErrorHandlerService,
    @Optional() @Inject(SMART_LINK_CONFIG) private readonly smartLinkConfig: SmartLinkConfig | null,
  ) {
    provideFaIcons([faPenToSquare, faEye]);
    merge(this.syncContentToEditor(), this.syncEditorToContentChange()).pipe(takeUntilDestroyed()).subscribe();
    effect(() => this.configurePopoverService());
    effect(() => this.formulaBookletUrlService.url.set(this.formulaBookletUrl()));
  }

  readonly content = input<ContentBlock[]>([]);
  readonly editable = input<boolean>(true);
  readonly showTableOfContents = input<boolean>(false);
  readonly contentPadding = input<string>('48px 96px');
  readonly imageUploadFn = input<UploadFunction>();
  readonly formulaBookletUrl = input<string>('');
  readonly resolveImageSrcFn = input<(src: string) => string>();
  readonly toRelativeImageSrcFn = input<(src: string) => string>();
  readonly collabConfig = input<CollabConfig>();

  readonly showPreviewToggle = input<boolean>(false);

  readonly contentChange = output<ContentBlock[]>();
  readonly editorReady = output<Editor>();

  readonly previewing = signal(false);
  readonly liveBlocks = signal<ContentBlock[]>([]);

  readonly editor = signal<Editor | null>(null);
  private readonly editorWrapper =
    viewChild<TiptapEditorWrapperComponent<TiptapEditorOutputFormat.Json>>('editorWrapper');

  readonly collabProvider = computed(() => this.editorWrapper()?.collabProvider() ?? null);
  readonly collabDoc = computed(() => this.editorWrapper()?.collabDoc() ?? null);
  private readonly collabSynced = computed(() => this.editorWrapper()?.collabSynced() ?? false);
  private readonly collabError = computed(() => this.editorWrapper()?.collabError() ?? null);

  private readonly collabState = computed(() => ({
    provider: this.collabProvider(),
    synced: this.collabSynced(),
    error: this.collabError(),
  }));

  readonly state = connectState({
    hasThreads: toObservable(this.collabProvider).pipe(
      switchMap((provider) => {
        if (!provider) {
          return of(false);
        }
        return observeThreads(provider).pipe(map((threads) => threads.length > 0));
      }),
      distinctUntilChanged(),
    ),
  });

  private readonly scrollableContainerRef = viewChild<ElementRef<HTMLElement>>('scrollableContainerRef');
  readonly scrollableContainer = computed(() => this.scrollableContainerRef()?.nativeElement);
  private readonly previewContentRef = viewChild<ElementRef<HTMLElement>>('previewContentRef');
  readonly previewContent = computed(() => this.previewContentRef()?.nativeElement);

  readonly editorControl = new FormControl<JSONContent>({}, { nonNullable: true });

  readonly editorOptions: Signal<Partial<Editor['options']>> = computed(() => ({
    extensions: [
      PreventBlockMergeExtension,
      KeyPointCalloutExtension(this.injector),
      SimpleCalloutExtension(this.injector),
      PopoverExtension(this.injector),
      SuperLatexExtension(this.injector),
      SolverExtension(this.injector),
      SimpleSolverExtension(this.injector),
      WidgetExtension(this.injector),
      CustomComponentExtension(this.injector),
      DataDisplayExtension(this.injector),
      FormulaCalloutExtension(this.injector),
      GojsDiagramExtension(this.injector),
      EchartsExtension(this.injector),
      AccordionExtension(this.injector),
      AccordionPanelExtension(this.injector),
      ColumnsExtension(this.injector),
      ColumnExtension(),
      CustomIconExtension(this.injector),
      ...(this.imageUploadFn()
        ? [
            ImageUploadNode.configure({
              accept: 'image/*',
              maxSize: MAX_FILE_SIZE,
              limit: 3,
              upload: this.stableImageUpload,
              onError: (error: Error) => console.error('Upload failed:', error),
            }),
          ]
        : []),
    ],
    editable: this.editable(),
    onCreate: ({ editor }: { editor: EditorWithPopoverService }) => {
      this.editor.set(editor);
      editor.storage.popoverService = this.popoverService;
      this.editorReady.emit(editor);
    },
  }));

  readonly tableOfContentsData = computed(() => this.editorWrapper()?.tableOfContentData() ?? []);

  readonly slashMenuItems = STUDY_NOTES_SLASH_MENU_ITEMS;
  readonly TiptapEditorOutputFormat = TiptapEditorOutputFormat;

  readonly reactSmartLinkConfig = computed<TiptapSmartLinkConfig | undefined>(() => {
    const smartLinkConfig = this.smartLinkConfig;
    if (!smartLinkConfig) {
      return undefined;
    }

    return runInInjectionContext(this.injector, () => toReactSmartLinkConfig(smartLinkConfig));
  });

  /**
   * Stable upload wrapper that delegates to the current `imageUploadFn` signal at call time.
   * This avoids the stale-closure problem: the Tiptap `useEditor` hook creates the editor
   * once and does not reconfigure extensions, so a closure captured at creation time would
   * always target the originally-selected file.
   */
  private readonly stableImageUpload: UploadFunction = (file, onProgress, abortSignal) => {
    const fn = this.imageUploadFn();
    if (!fn) {
      return Promise.reject(new Error('No image upload function configured'));
    }
    return fn(file, onProgress, abortSignal);
  };

  private syncContentToEditor() {
    const content$ = toObservable(this.content).pipe(distinctUntilDeepChanged());
    const editor$ = toObservable(this.editor).pipe(filter(isNotNullish));
    const collabState$ = toObservable(this.collabState);

    return toObservable(this.collabConfig).pipe(
      distinctUntilChanged((a, b) => a?.roomId === b?.roomId),
      switchMap((collabConfig) => {
        if (!collabConfig) {
          return combineLatest([content$, editor$]).pipe(tap(([blocks]) => this.applyContent(blocks)));
        }

        const editorSnapshot = this.editor();
        const collabProviderSnapshot = this.collabProvider();
        const collabErrorSnapshot = this.collabError();

        // On page switch the React key change destroys and recreates the editor,
        // so we must skip the stale instance and wait for the fresh one.
        const editorChanges$ = editorSnapshot ? editor$.pipe(filter((editor) => editor !== editorSnapshot)) : editor$;

        const collabReady$ = collabState$.pipe(
          filter((state) => {
            if (isNotNullish(state.provider)) {
              return state.provider !== collabProviderSnapshot && (state.synced || isNotNullish(state.error));
            }
            // Collab setup failed (e.g. token unavailable): provider stays null
            // but a new error distinguishes this from the initial idle state.
            return isNotNullish(state.error) && state.error !== collabErrorSnapshot;
          }),
          take(1),
        );

        return concat(
          // Phase 1: wait for collab sync, seed the room only if it has no content yet.
          combineLatest([content$, editorChanges$, collabReady$]).pipe(
            take(1),
            tap(([blocks, editor]) => {
              // Skip seeding when the collab provider errored (auth failure,
              // disconnect) AND the editor still has the Collaboration extension,
              // because writes go to a broken Y.js doc that won't sync.
              // If the React layer already fell back to a plain editor (no
              // Collaboration extension), seeding is safe and expected.
              if (this.collabError() && this.isCollaborativeEditor(editor)) {
                return;
              }

              this.seedContentToCollabRoomIfNeeded(blocks, editor);
            }),
          ),
          // Phase 2: forward any subsequent content the consumer pushes.
          combineLatest([content$, editor$]).pipe(
            filter(() => this.collabConfig()?.roomId === collabConfig?.roomId),
            // Once collaboration is active, the shared Y.js document becomes the
            // source of truth. Only keep forwarding Angular-side content updates
            // when the React layer has already fallen back to a plain editor.
            filter(([, editor]) => !this.isCollaborativeEditor(editor)),
            tap(([blocks]) => this.applyContent(blocks)),
          ),
        );
      }),
    );
  }

  private seedContentToCollabRoomIfNeeded(blocks: ContentBlock[], editor: Editor): void {
    if (blocks.length === 0 || !editor.isEmpty) {
      return;
    }

    const ydoc = this.collabDoc();
    if (!ydoc) {
      this.applyContent(blocks);
      return;
    }

    ydoc.transact(() => {
      if (isCollabInitialContentSeeded(ydoc)) {
        return;
      }

      if (!this.applyContent(blocks)) {
        return;
      }

      markCollabInitialContentSeeded(ydoc);
    });
  }

  private isCollaborativeEditor(editor: Editor): boolean {
    return editor.extensionManager.extensions.some((ext) => ext.name === 'collaboration');
  }

  private applyContent(blocks: ContentBlock[]): boolean {
    try {
      const transformImageSrcFn = this.resolveImageSrcFn();
      this.editorControl.setValue(
        contentBlocksToTiptap(blocks, transformImageSrcFn ? { transformImageSrc: transformImageSrcFn } : undefined),
        {
          emitEvent: false,
        },
      );
      this.liveBlocks.set(blocks);
      return true;
    } catch (err) {
      this.errorHandlerService.error(
        `[StudyNotesEditorComponent.applyContent]: 'Failed to convert ContentBlock[] to Tiptap`,
        { err },
      );
      return false;
    }
  }

  private syncEditorToContentChange() {
    return this.editorControl.valueChanges.pipe(
      debounceTime(300),
      map((value) => {
        const editor = this.editor();
        if (!editor) {
          return null;
        }
        try {
          const strip = this.toRelativeImageSrcFn();
          return tiptapToContentBlocks(value, strip ? { transformImageSrc: strip } : undefined);
        } catch (err) {
          console.error('Failed to convert Tiptap to ContentBlock[]:', err);
          return null;
        }
      }),
      filter(isNotNullish),
      distinctUntilDeepChanged(),
      tap((blocks) => {
        this.liveBlocks.set(blocks);
        this.contentChange.emit(blocks);
      }),
    );
  }

  private configurePopoverService(): void {
    this.popoverService.config = {
      allowEditing: this.editable(),
      editorOptions: {
        extensions: [
          PreventBlockMergeExtension,
          KeyPointCalloutExtension(this.injector),
          SimpleCalloutExtension(this.injector),
          SuperLatexExtension(this.injector),
          SolverExtension(this.injector),
        ],
      },
      slashMenuItems: this.slashMenuItems,
      onContentChange: (popoverId, jsonContent) => {
        let parsedContent;
        try {
          parsedContent = JSON.parse(jsonContent);
        } catch {
          console.error('Failed to parse popover content change:', jsonContent);
          return;
        }
        this.editor()?.commands.updatePopoverById(popoverId, { jsonContent: parsedContent });
      },
      onLinkUrlChange: (popoverId, linkUrl) => {
        this.editor()?.commands.updatePopoverById(popoverId, { linkUrl: linkUrl || undefined });
      },
    };
  }
}
